/**
 * Where relationship disagreements are surfaced (canon 12 Rule 17):
 *
 *   - RelationshipSplitsPanel — where several rankings meet (the Tracks of a
 *     Cycle). A package chosen in part, an item chosen without what it needs,
 *     a whole chosen with its parts, the same thing ranked twice.
 *   - CompetingChoiceNotice — inside one ranking. Competing items are alike,
 *     so the choice between them is made in the Track.
 *
 * Both tell; neither repairs. Each finding links back to the rankings it came
 * from so it can be sent back down.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Callout } from '@/components/ds';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useRelationshipGraph } from '../../hooks/prioritization/useRelationshipGraph';
import {
  findCompetingBothChosen,
  findSplitsWhereRankingsMeet,
  meetingFindingSentence,
  type RankedResult,
} from '../../lib/itemRelationshipFlags';

function useItemTitle() {
  const items = useItemsStore((s) => s.items);
  return (id: string) => items.find((i) => i.id === id)?.title ?? 'an item you cannot see';
}

interface RelationshipSplitsPanelProps {
  /** One entry per ranking that meets here — normally one per Track. */
  results: RankedResult[];
  /** Where a ranking can be opened, so the finding can go back to it. */
  linkFor?: (resultId: string) => string | undefined;
  className?: string;
}

export function RelationshipSplitsPanel({ results, linkFor, className }: RelationshipSplitsPanelProps) {
  const graph = useRelationshipGraph();
  const titleOf = useItemTitle();
  const findings = useMemo(() => findSplitsWhereRankingsMeet(results, graph), [results, graph]);
  if (findings.length === 0) return null;

  const nameOf = (id: string) => results.find((r) => r.id === id)?.name ?? id;

  return (
    <section className={className} data-testid="relationship-splits-panel">
      <h2 className="mb-1 text-lg font-semibold text-[var(--text-pri)]">Results that disagree</h2>
      <p className="mb-3 text-sm text-[var(--text-sec)]">
        These items are linked, and their results do not line up. Nothing was changed. Send each one back to the
        people who ranked it.
      </p>
      <div className="space-y-2">
        {findings.map((f) => {
          const { title, detail } = meetingFindingSentence(f, titleOf);
          return (
            <Callout key={`${f.kind}:${f.itemIds.join(',')}`} tone="mustard" title={title}>
              <p>{detail}</p>
              {f.resultIds.length > 0 && (
                <p className="mt-1">
                  Ranked in:{' '}
                  {f.resultIds.map((id, i) => {
                    const to = linkFor?.(id);
                    return (
                      <span key={id}>
                        {i > 0 && ', '}
                        {to ? (
                          <Link to={to} className="underline hover:text-[var(--text-pri)]">
                            {nameOf(id)}
                          </Link>
                        ) : (
                          nameOf(id)
                        )}
                      </span>
                    );
                  })}
                </p>
              )}
            </Callout>
          );
        })}
      </div>
    </section>
  );
}

export function CompetingChoiceNotice({ result, className }: { result: RankedResult; className?: string }) {
  const graph = useRelationshipGraph();
  const titleOf = useItemTitle();
  const sets = useMemo(() => findCompetingBothChosen(result, graph), [result, graph]);
  if (sets.length === 0) return null;

  return (
    <div className={className} data-testid="competing-choice-notice">
      {sets.map((ids) => (
        <Callout key={ids.join(',')} tone="mustard" title="Competing items were both chosen">
          {ids.map(titleOf).join(' and ')} compete. Only one of them can be chosen. Choose one here before this result
          goes up.
        </Callout>
      ))}
    </div>
  );
}
